import * as THREE from 'three';

import { SkyState } from '../../sky/Daylight';
import { captureScene, packScene, type SceneBuffers } from './Scene';
import present from './present.wgsl?raw';
import trace from './trace.wgsl?raw';

/**
 * A path traced view of the world, on its own canvas, in WebGPU.
 *
 * **Its own canvas is the whole safety argument.** The game renders through
 * WebGL and nothing here touches it: this is a second surface laid over the
 * top, with a device of its own, that can fail to initialise or be switched off
 * without the game noticing. A debug feature that can break the thing it is
 * debugging is worse than no debug feature.
 *
 * Traced at a fraction of the game's buffer and scaled up by the browser, which
 * is not a compromise so much as a fit: the game already renders small and
 * upscales by a whole number, so a coarse trace looks like the same picture
 * rather than like a different one.
 *
 * What it is not: a renderer. There is one bounce of indirect light by default,
 * two materials, no textures and no importance sampling worth the name. It
 * exists to answer "what would this world look like if the light were actually
 * traced", and the answer is legible long before the maths is respectable.
 */

/** Traced pixels per game pixel, on each axis. */
const SCALE = 0.5;

/** Paths per pixel per pass. Interactive means few. */
const SAMPLES = 2;

/** Diffuse bounces after the first hit. One is enough to see colour bleeding. */
const BOUNCES = 1;

/** How far the geometry can move before what was accumulated is a lie. */
const RESET_EPSILON = 1e-3;

export interface TracerStats {
  geometries: number;
  instances: number;
  triangles: number;
  passes: number;
  width: number;
  height: number;
  buildMs: number;
}

export class PathTracer {
  readonly canvas = document.createElement('canvas');

  private device?: GPUDevice;
  private context?: GPUCanvasContext;
  private computePipeline?: GPUComputePipeline;
  private presentPipeline?: GPURenderPipeline;
  private computeGroup?: GPUBindGroup;
  private presentGroup?: GPUBindGroup;

  private params?: GPUBuffer;
  private presentParams?: GPUBuffer;
  private accumulator?: GPUBuffer;
  private scene: GPUBuffer[] = [];

  private width = 0;
  private height = 0;
  private passes = 0;
  private captured?: SceneBuffers;
  private buildMs = 0;
  private lastView = new Float32Array(20);
  private uniforms = new Float32Array(40);

  constructor(private readonly container: HTMLElement) {
    this.canvas.className = 'pathtrace';
    this.canvas.hidden = true;
    this.container.appendChild(this.canvas);
  }

  get available(): boolean {
    return 'gpu' in navigator;
  }

  get stats(): TracerStats {
    return {
      geometries: this.captured?.stats.geometries ?? 0,
      instances: this.captured?.stats.instances ?? 0,
      triangles: this.captured?.stats.triangles ?? 0,
      passes: this.passes,
      width: this.width,
      height: this.height,
      buildMs: this.buildMs,
    };
  }

  /**
   * Ask the browser for a device.
   *
   * Separate from the constructor because it is asynchronous and can simply
   * fail — no WebGPU, no adapter, a device lost on creation. Every one of those
   * is a `false` and a line in the console, never a thrown error: the game is
   * running and this is a key somebody pressed.
   */
  async open(): Promise<boolean> {
    if (this.device) return true;
    if (!this.available) {
      console.warn('🔷 path tracing: ta przeglądarka nie ma WebGPU');
      return false;
    }

    try {
      const adapter = await navigator.gpu.requestAdapter({
        powerPreference: 'high-performance',
      });
      if (!adapter) {
        console.warn('🔷 path tracing: brak adaptera WebGPU');
        return false;
      }

      // Asked for rather than assumed: a scene of any size needs a storage
      // buffer bigger than the 128 MB the spec floor guarantees, and finding
      // that out at bind time is finding it out too late.
      const wanted = Math.min(
        adapter.limits.maxStorageBufferBindingSize,
        512 * 1024 * 1024
      );
      this.device = await adapter.requestDevice({
        requiredLimits: { maxStorageBufferBindingSize: wanted },
      });
      this.device.lost.then((info) => {
        console.warn(`🔷 path tracing: urządzenie przepadło (${info.reason})`);
        this.device = undefined;
      });

      const context = this.canvas.getContext('webgpu');
      if (!context) {
        console.warn('🔷 path tracing: canvas nie dał kontekstu webgpu');
        return false;
      }
      this.context = context;
      this.context.configure({
        device: this.device,
        format: navigator.gpu.getPreferredCanvasFormat(),
        alphaMode: 'opaque',
      });

      this.buildPipelines();
      return true;
    } catch (error) {
      console.warn('🔷 path tracing:', error);
      return false;
    }
  }

  /**
   * Take the scene as it stands and build the hierarchies over it.
   *
   * Called on switching on and not per frame: this is the expensive half and
   * the world only changes when terrain streams in or something is built. The
   * cost is reported, because it is the number that decides whether this is
   * usable at a given draw radius.
   */
  capture(scene: THREE.Object3D, isWater: (mesh: THREE.Mesh) => boolean): void {
    if (!this.device) return;

    const started = performance.now();
    const captured = captureScene(scene, isWater);
    this.buildMs = performance.now() - started;
    this.captured = captured;

    for (const buffer of this.scene) buffer.destroy();
    this.scene = [];

    // Packed by `Scene.packScene`, which is also what the layout test walks —
    // the offsets below are the only thing standing between a correct picture
    // and a subtly wrong one, so they are computed in one place and checked.
    const packed = packScene(captured);
    this.bases = [packed.bases[0], packed.bases[1], packed.bases[2], SAMPLES];



    this.scene = [
      this.storage(packed.geometry),
      this.storage(packed.nodes),
      this.storage(packed.ints),
      this.storage(packed.instances),
    ];

    this.passes = 0;
    this.computeGroup = undefined;
  }

  private bases: [number, number, number, number] = [0, 0, 0, SAMPLES];

  /**
   * One pass over the picture.
   *
   * The accumulator is thrown away whenever the camera moves, because a sum of
   * samples taken from two places is not a picture of either. Standing still is
   * therefore what makes this converge, and the sample counter in the read-out
   * is the honest measure of how far along it is.
   */
  render(camera: THREE.OrthographicCamera, sky: SkyState): void {
    const device = this.device;
    const context = this.context;
    if (!device || !context || !this.computePipeline || !this.presentPipeline) return;
    if (!this.captured || this.captured.instanceCount === 0) return;

    this.resize();
    if (this.width === 0 || this.height === 0) return;

    const moved = this.writeUniforms(camera, sky);
    if (moved) this.reset();

    device.queue.writeBuffer(this.params!, 0, this.uniforms);

    const encoder = device.createCommandEncoder();

    const compute = encoder.beginComputePass();
    compute.setPipeline(this.computePipeline);
    compute.setBindGroup(0, this.bindCompute());
    compute.dispatchWorkgroups(
      Math.ceil(this.width / 8),
      Math.ceil(this.height / 8)
    );
    compute.end();

    const pass = encoder.beginRenderPass({
      colorAttachments: [
        {
          view: context.getCurrentTexture().createView(),
          loadOp: 'clear',
          storeOp: 'store',
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
        },
      ],
    });
    pass.setPipeline(this.presentPipeline);
    pass.setBindGroup(0, this.presentGroup!);
    pass.draw(3);
    pass.end();

    device.queue.submit([encoder.finish()]);
    this.passes += SAMPLES;
  }

  show(): void {
    this.canvas.hidden = false;
  }

  hide(): void {
    this.canvas.hidden = true;
  }

  dispose(): void {
    for (const buffer of this.scene) buffer.destroy();
    this.scene = [];
    this.params?.destroy();
    this.presentParams?.destroy();
    this.accumulator?.destroy();
    this.canvas.remove();
    this.device?.destroy();
    this.device = undefined;
  }

  private buildPipelines(): void {
    const device = this.device!;

    this.computePipeline = device.createComputePipeline({
      layout: 'auto',
      compute: { module: device.createShaderModule({ code: trace }), entryPoint: 'main' },
    });

    const module = device.createShaderModule({ code: present });
    this.presentPipeline = device.createRenderPipeline({
      layout: 'auto',
      vertex: { module, entryPoint: 'vertexMain' },
      fragment: {
        module,
        entryPoint: 'fragmentMain',
        targets: [{ format: navigator.gpu.getPreferredCanvasFormat() }],
      },
      primitive: { topology: 'triangle-list' },
    });

    this.params = device.createBuffer({
      size: this.uniforms.byteLength,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.presentParams = device.createBuffer({
      size: 16,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
  }

  private storage(data: Float32Array | Uint32Array): GPUBuffer {
    const device = this.device!;
    const buffer = device.createBuffer({
      size: Math.max(4, data.byteLength),
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    // The cast is TypeScript's, not WebGPU's: since 5.7 a typed array is
    // generic over the kind of buffer behind it, and one built without saying
    // so could in principle be sitting on a `SharedArrayBuffer`. None of these
    // is — they are all made a few lines above — so the alternative would be
    // threading a type parameter through the whole capture for nothing.
    device.queue.writeBuffer(buffer, 0, data as unknown as GPUAllowSharedBufferSource);
    return buffer;
  }

  /** The traced buffer follows the window, at a fraction of its size. */
  private resize(): void {
    const width = Math.max(1, Math.floor(this.container.clientWidth * SCALE));
    const height = Math.max(1, Math.floor(this.container.clientHeight * SCALE));
    if (width === this.width && height === this.height && this.accumulator) return;

    this.width = width;
    this.height = height;
    this.canvas.width = width;
    this.canvas.height = height;

    this.accumulator?.destroy();
    this.accumulator = this.device!.createBuffer({
      size: width * height * 16,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });

    this.device!.queue.writeBuffer(
      this.presentParams!,
      0,
      new Uint32Array([width, height, 0, 0])
    );

    this.presentGroup = this.device!.createBindGroup({
      layout: this.presentPipeline!.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: this.presentParams! } },
        { binding: 1, resource: { buffer: this.accumulator } },
      ],
    });

    this.computeGroup = undefined;
    this.reset();
  }

  private bindCompute(): GPUBindGroup {
    if (this.computeGroup) return this.computeGroup;

    this.computeGroup = this.device!.createBindGroup({
      layout: this.computePipeline!.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: this.params! } },
        { binding: 1, resource: { buffer: this.scene[0] } },
        { binding: 2, resource: { buffer: this.scene[1] } },
        { binding: 3, resource: { buffer: this.scene[2] } },
        { binding: 4, resource: { buffer: this.scene[3] } },
        { binding: 5, resource: { buffer: this.accumulator! } },
      ],
    });
    return this.computeGroup;
  }

  private reset(): void {
    this.passes = 0;
    if (!this.accumulator) return;
    // Zeroed rather than left to be overwritten: the shader adds to what is
    // there, so stale sums would keep bleeding into the new picture.
    const clear = new Float32Array(this.width * this.height * 4);
    this.device!.queue.writeBuffer(this.accumulator, 0, clear);
  }

  /**
   * Camera and light into the uniform block, and a word on whether the view
   * moved.
   *
   * The comparison is on what the rays are actually made of — the plane's
   * centre, its two steps and the direction — rather than on the camera's
   * fields. Two different zooms can put the plane in the same place, and a
   * reset the eye cannot see is a reset that throws away a converged picture.
   */
  private writeUniforms(camera: THREE.OrthographicCamera, sky: SkyState): boolean {
    const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
    const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1);
    const forward = new THREE.Vector3()
      .setFromMatrixColumn(camera.matrixWorld, 2)
      .negate();

    // The game's camera is orthographic, so a pixel is a step across the plane
    // and not an angle. Its own frustum is in world units already; dividing by
    // the traced size is what makes one traced pixel cover one traced pixel.
    const across = (camera.right - camera.left) / camera.zoom / this.width;
    const down = (camera.top - camera.bottom) / camera.zoom / this.height;

    const stepRight = right.clone().multiplyScalar(across);
    // Screen y grows downwards and the world's up does not.
    const stepUp = up.clone().multiplyScalar(-down);

    const view = new Float32Array([
      camera.position.x, camera.position.y, camera.position.z,
      stepRight.x, stepRight.y, stepRight.z,
      stepUp.x, stepUp.y, stepUp.z,
      forward.x, forward.y, forward.z,
      sky.direction.x, sky.direction.y, sky.direction.z,
      sky.sunIntensity,
      sky.skyColor.r, sky.skyColor.g, sky.skyColor.b,
      sky.ambientIntensity,
    ]);

    let moved = false;
    for (let i = 0; i < view.length; i++) {
      if (Math.abs(view[i] - this.lastView[i]) > RESET_EPSILON) {
        moved = true;
        break;
      }
    }
    this.lastView = view;

    const u = this.uniforms;
    u.set([camera.position.x, camera.position.y, camera.position.z, 0], 0);
    u.set([stepRight.x, stepRight.y, stepRight.z, 0], 4);
    u.set([stepUp.x, stepUp.y, stepUp.z, 0], 8);
    // The fourth component is how far back along the ray to start. The game's
    // near plane is negative so that ground behind the camera is still drawn;
    // the same distance has to be given back here or the near half of the view
    // would trace nothing.
    u.set([forward.x, forward.y, forward.z, Math.max(0, -camera.near)], 12);
    u.set(
      [sky.direction.x, sky.direction.y, sky.direction.z, sky.sunIntensity],
      16
    );
    u.set([sky.sunColor.r, sky.sunColor.g, sky.sunColor.b, 0], 20);
    // The sky as two colours: what is overhead and what is towards the ground.
    // The same pair the raster renderer lights with, so the two pictures are
    // lit by the same weather rather than by two guesses at it.
    const ambient = sky.ambientIntensity;
    u.set(
      [sky.skyColor.r * ambient, sky.skyColor.g * ambient, sky.skyColor.b * ambient, 0],
      24
    );
    u.set(
      [
        sky.groundColor.r * ambient,
        sky.groundColor.g * ambient,
        sky.groundColor.b * ambient,
        0,
      ],
      28
    );

    const dims = new Uint32Array(u.buffer, u.byteOffset + 32 * 4, 8);
    dims[0] = this.width;
    dims[1] = this.height;
    dims[2] = this.passes;
    dims[3] = BOUNCES;
    dims[4] = this.bases[0];
    dims[5] = this.bases[1];
    dims[6] = this.bases[2];
    dims[7] = this.bases[3];

    return moved;
  }
}
